<template>
  <div>
    <div :class="$style.top">
      <i
        :class="$style.top_icon"
        class="fa-regular fa-comment-lines"/>
      <div :class="$style.top_text"><span :class="$style.nowrap">事務局個別メッセージ詳細</span></div>
    </div>

    <div v-if="flag.invalid">
      <spacer :y="10"/>
      <h3>URLが誤っています。ホームから再度アクセスし直してください。</h3>
    </div>

    <loader-simple :flag="flag.loader">
      <spacer :y="4"/>
      <!-- 宛先(To)は事務局→組織の学校共有ルーム(type=4)でだけ扱う。既定は「指定なし」（全員へお知らせ）で、宛先を決めたいときだけ
           「To を指定する」に切り替えて選ぶ（複数可。指定は明示的な操作にして、うっかり絞り込んだまま送らないようにする）。
           発言には名前の行に「事務局 → ○○さん（所属等）」、管理者の画面では自分が宛先なら「あなた」と出す。
           事務局⇔個人(type=2)・管理者の送信には宛先欄を出さない（サーバも type=4 の事務局送信以外の To は保存しない） -->
      <contents-box v-if="!flag.loader && !flag.invalid" title="メッセージ登録">
        <!-- メールと同じく「宛先 → 本文」の順に置く -->
        <!-- 「To」の見出しは置かず（切替ボタンの「To を指定する」で分かる・顧客の依頼）、本文欄の左端にそろえる -->
        <div v-if="isToSelectShown">
          <div :class="$style.toModes" role="radiogroup" :aria-label="toWording.heading">
            <button
              v-for="mode in toModes"
              :key="mode.value"
              type="button"
              role="radio"
              :aria-checked="toMode === mode.value"
              :class="[$style.toMode, toMode === mode.value ? $style.selected : '']"
              @click="setToMode(mode.value)">{{ mode.label }}</button>
          </div>
          <!-- 候補は本文欄の幅で 2 列。管理者を 1 段目に置き、副管理者は 2 段目から 2 人ずつ並べる（いない分の枠は作らない） -->
          <template v-if="toMode === 'pick'">
            <ul
              v-for="group in toCandidateGroups"
              :key="group.key"
              :class="$style.toCards">
              <li v-for="candidate in group.candidates" :key="candidate.id">
                <button
                  type="button"
                  :aria-pressed="toUserIds.includes(candidate.id)"
                  :class="[$style.toCard, toUserIds.includes(candidate.id) ? $style.selected : '']"
                  @click="toggleToUser(candidate.id)">
                  <span :class="$style.toCardRole">{{ getToCandidateRole(candidate) }}</span>
                  <!-- 氏名だけでは役割が分からないので、投稿者の氏名の横と同じ所属等を添える（カッコは付けない）。
                       副管理者は所属等を氏名の横に置いてカードを低くし、収まらなければ所属等ごと次の行へ回す。
                       管理者は氏名の下のまま（顧客の依頼が副管理者だけ）。スマホは全員氏名の下 -->
                  <span :class="[$style.toCardBody, group.key === 'sub' ? $style.toCardBodyInline : '']">
                    <span :class="$style.toCardName">{{ candidate.username }}</span>
                    <span :class="$style.toCardAffiliation">{{ getToCandidateAffiliation(candidate) }}</span>
                  </span>
                  <span :class="$style.toCardCheck" aria-hidden="true"></span>
                </button>
              </li>
            </ul>
          </template>
          <spacer :y="2"/>
        </div>
        <div>
          <textarea
            :class="$style.textarea"
            :disabled="flag.isConfirm"
            placeholder="内容を入力してください"
            v-model="message">
          </textarea>
        </div>
        <spacer :y="3"/>

        <btn-container
          v-if="!flag.isConfirm">
          <basic-btn
            tag="button"
            v-on:click="submit">登録する</basic-btn>
          <spacer :x="2"/>
        </btn-container>
      </contents-box>
      <spacer :y="4"/>
      <contents-box v-if="!flag.loader && !flag.invalid && data && data.messages.length">
        <ul>
          <div :class="$style.comment" v-for="(chat, i) in data.messages" :key="i">
            <stack-item
              :class="chat.user.account_type === 31 ? $style.q : $style.a">
              <div :class="[chat.user.account_type === 31 ? $style.question : $style.answer, isSchoolRoom && isMessageAddressedToViewer(chat, user) ? $style.toViewer : '']">
                <!-- 未確認メッセージに NEW マーク（学校管理者/事務局のみ・isUnreadMessage で判定・#3185） -->
                <div v-if="isUnreadMessage(chat)" :class="$style.new">NEW</div>
                <!-- 氏名＋F情報。F情報は学校管理者(11/12)のみ氏名右にインライン表示・未設定は（要更新）・他ロールは非表示。
                     宛先(To)があれば氏名の後ろに「→ ○○さん（所属等）」（学校共有ルームのみ・本文の v-html とは別にテキストで描く。
                     無効になった宛先の所属等は投稿者と同じく灰色） -->
                <div :class="[$style.name, isInactivePostUser(chat) ? $style.inactiveName : '']">{{ getPostUserDisplayName(chat) }}<span v-if="getPostUserAffiliation(chat)" :class="[$style.affiliation, isInactivePostUser(chat) ? $style.inactiveAffiliation : '']">{{ getPostUserAffiliation(chat) }}</span><span v-if="isInactivePostUser(chat)" :class="$style.inactiveLabel">（無効）</span><template v-if="isSchoolRoom && getMessageToNames(chat, user).length"><span :class="$style.toArrow" aria-label="宛先">→</span><span :class="$style.toNames"><template v-for="(toName, ti) in getMessageToNames(chat, user)" :key="toName.key">{{ ti ? '、' : '' }}<span :class="toName.isViewer ? $style.toMe : ''">{{ toName.label }}</span><span v-if="toName.affiliation" :class="[$style.affiliation, $style.toAffiliation, toName.inactiveLabel ? $style.inactiveAffiliation : '']">{{ toName.affiliation }}</span><span v-if="toName.inactiveLabel" :class="$style.inactiveLabel">{{ toName.inactiveLabel }}</span></template></span></template> [{{ filterMoment(chat.updated_at, 'YYYY.MM.DD HH:mm') }}]</div>
                <spacer :y="1"/>
                <div class="n2br">
                  <p v-html="chat.message"></p>
                </div>
              </div>
            </stack-item>
          </div>
        </ul>
      </contents-box>

    <spacer :y="4"/>
    </loader-simple>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ContentsBox from '@/views/components/ContentsBox.vue';
import StackItem from '@/views/components/StackItem.vue';
import Spacer from '@/views/components/Spacer.vue';
import LoaderSimple from '@/views/components/LoaderSimple.vue';
import BasicBtn from '@/views/components/BasicBtn.vue';
import BtnContainer from '@/views/components/BtnContainer.vue';
import cf from '@/mixins/commonFunctions';
import messageTo, { TO_WORDING } from '@/mixins/messageTo';

export default {
  name: 'message-detail',
  mixins: [cf, messageTo],
  components: {
    ContentsBox,
    StackItem,
    Spacer,
    LoaderSimple,
    BasicBtn,
    BtnContainer,
  },
  data() {
    return {
      id: null,
      data: null,
      message: null,
      // 既読登録"前"の readflags スナップショット。NEW 判定はこれを基準にし、
      // 開いた当回は NEW を保持・次回ロードで消える挙動にする（#3185）
      readSnapshot: [],
      // 既読登録の二重実行防止（getDetail は submit 後に再呼び出しされるため・#3185）
      readFlagHandled: false,
      // To に選べる人（組織のログイン可能な管理者・事務局×学校共有ルームのときだけ取得）と、選んだ人の users.id
      toCandidates: [],
      toCandidatesLoaded: false,
      toUserIds: [],
      // 'all'＝指定なし（To なし・全員へお知らせ）・'pick'＝To を指定する。指定は明示的に切り替えたときだけ
      toMode: 'all',
      flag: {
        loader: true,
        invalid: false,
      },
    };
  },
  created() {
    this.id = cf.convert2Num(this.$route.params.slug);
    if (this.user.email) {
      this.getDetail();
    } else {
      this.$store.subscribe((mutation) => {
        if (mutation.type === 'user/setUserData') {
          this.getDetail();
        }
      });
    }
  },
  computed: {
    ...mapState(['user', 'helper']),
    // NEW マーク対象ロール。学校管理者(11/12)・事務局(31)のみ。
    // 当画面は利用者(/text)・医師心理士(/counselor/text・現状 dead)とも共有のため、
    // 利用者・医師心理士には NEW を出さない（表示制約・既存挙動を変えない・#3185）
    isMessageNewTarget() {
      return [11, 12, 31].includes(this.user.account_type);
    },
    // 事務局↔組織の学校共有ルーム(type=4)か。To の表示・選択はこのルームに限る（type=2 の個別メッセージには出さない）
    isSchoolRoom() {
      return !!this.data && this.data.target_type === 4;
    },
    // To を選べるのは事務局(31)が学校共有ルームへ送るときだけ
    isToSelectable() {
      return this.isSchoolRoom && this.user.account_type === 31;
    },
    // 候補が 2 人未満なら To の欄は出さない。指定なしでも指定でも届く相手は同じ（1 人ならその人へ）で、選ばせる意味が無いため
    isToSelectShown() {
      return this.isToSelectable && this.toCandidates.length >= 2;
    },
    toWording() {
      return TO_WORDING;
    },
    toModes() {
      return [
        { value: 'all', label: TO_WORDING.all },
        { value: 'pick', label: TO_WORDING.pick },
      ];
    },
    // To の候補を 管理者(11) と それ以外（副管理者 12）に分ける。段を分けて描くので、副管理者は必ず管理者の次の段から始まる
    toCandidateGroups() {
      const groups = [
        { key: 'admin', candidates: this.toCandidates.filter((candidate) => candidate.account_type === 11) },
        { key: 'sub', candidates: this.toCandidates.filter((candidate) => candidate.account_type !== 11) },
      ];
      return groups.filter((group) => group.candidates.length);
    },
  },
  methods: {
    /**
     * 未確認メッセージ判定。未読（readSnapshot 未登録）かつ自分以外の投稿。
     * 対象ロール(11/12/31)以外は常に false。（#3185）
     */
    isUnreadMessage(chat) {
      if (!this.isMessageNewTarget) return false;
      if (!this.user.readflags || !this.user.readflags.operatorMessages) return false;
      return !this.readSnapshot.includes(chat.id) && chat.user_id !== this.user.id;
    },
    /**
     * 詳細表示時に未読メッセージを既読登録（model_type=6）。
     * 次回開いた時に NEW が消えるようにする。当回は user/update せず NEW を保持（参照: RiskApplicationForm.registMessageReadFlag）。
     * 二重実行は readFlagHandled で防止。（#3185）
     */
    async registerMessageReadFlags() {
      if (this.readFlagHandled || !this.isMessageNewTarget) return;
      if (!this.data || !this.data.messages || !this.user.readflags) return;
      this.readFlagHandled = true;
      const readIds = this.user.readflags.operatorMessages || [];
      const targets = this.data.messages.filter(
        (chat) => chat.user_id !== this.user.id && !readIds.includes(chat.id),
      );
      if (targets.length === 0) return;
      await Promise.all(targets.map((chat) => this.axios({
        method: 'POST',
        url: '/v1/readflag/set/register',
        data: { model_type: 6, user_id: this.user.id, foreign_id: chat.id },
      }).catch((error) => {
        if (error.message) console.log(error.message);
        else console.log(error);
      })));
    },
    /** ローディング表示 */
    showLoading() {
      const args = { modalName: 'modalLoadingBallScaleRippleMultiple' };
      this.$store.dispatch('modal/loadings/showModal', args, { root: true });
    },
    /** ローディング非表示 */
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },
    getDetail() {
      const detailRequest = this.axios({
        method: 'GET',
        url: '/v1/messagegroup/get/detail',
        params: {
          id: this.id,
          isOperator: 1,
        },
      });
      // 事務局は To の選択肢も同時に取りにいき、両方そろってから画面を出す（後から To の欄が差し込まれて入力欄がずれないように）。
      // 学校共有ルームかどうかは詳細を取るまで分からないが、ほかのルームではサーバが空の一覧を返すだけ
      Promise.all([detailRequest, this.getToCandidates()])
        .then(([response]) => {
          this.data = response.data.detail;
          this.flag.loader = false;
          if (!this.data) {
            this.flag.invalid = true;
          } else if (this.data.target_type === 4) {
            // type=4 学校共有ルーム: 事務局(31) または 同一校の学校管理者(11/12) のみ開ける（#3185 ②・相互閲覧）。
            // B 決定により backend 認可ガードは足さず、ここの frontend ガードで制御（副管理者が自校スレッドを開けるように）。
            const mySchoolId = this.user.school && this.user.school.length ? this.user.school[0].id : null;
            const isOperator = this.user.account_type === 31;
            const isSameSchoolAdmin = [11, 12].includes(this.user.account_type) && mySchoolId === this.data.school_id;
            if (!isOperator && !isSameSchoolAdmin) {
              this.flag.invalid = true;
            }
          } else if (this.data.user_id !== this.user.id && this.data.target_user_id !== this.user.id) {
            // type=2 (従来): 当事者2人（作成者=事務局 / 対象者）のみ
            this.flag.invalid = true;
          }
          // 初回ロード時に既読登録"前"のスナップショットを取り、未読を既読登録（#3185）
          if (!this.flag.invalid && !this.readFlagHandled && this.isMessageNewTarget) {
            this.readSnapshot = this.user.readflags
              ? [...(this.user.readflags.operatorMessages || [])]
              : [];
            this.registerMessageReadFlags();
          }
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
        });
    },

    /**
     * To に選べる人（組織のログイン可能な主管理者・副管理者）を取得する。事務局のときだけ・1 回だけ取りにいく
     * （送信後の詳細の再取得では取り直さない）。
     * 取得に失敗しても To の欄が出ないだけで、To なし（全員へお知らせ）の送信はできる。詳細の表示を巻き込まないよう reject しない。
     * @returns {Promise<void>}
     */
    getToCandidates() {
      if (this.user.account_type !== 31 || this.toCandidatesLoaded) return Promise.resolve();
      this.toCandidatesLoaded = true;
      return this.axios({
        method: 'GET',
        url: '/v1/messagegroup/get/toCandidates',
        params: { id: this.id },
      })
        .then((response) => {
          this.toCandidates = response.data.candidates || [];
        })
        .catch((error) => {
          // 失敗したら次の詳細再取得（送信後など）で取り直せるように戻す
          this.toCandidatesLoaded = false;
          if (error.response) console.log(error.response.data);
          else console.log(error);
        });
    },

    /** 「指定なし」に戻したら選んだ人も外す（画面に見えていない選択を残して送らない） */
    setToMode(mode) {
      this.toMode = mode;
      if (mode !== 'pick') this.toUserIds = [];
    },
    toggleToUser(id) {
      this.toUserIds = this.toUserIds.includes(id)
        ? this.toUserIds.filter((selectedId) => selectedId !== id)
        : [...this.toUserIds, id];
    },

    /** 選択中の To を、確認ダイアログに出す「To: 管理者・氏名、…」の 1 行にする（未選択なら ''） */
    selectedToConfirmLine() {
      const selected = this.toCandidates.filter((candidate) => this.toUserIds.includes(candidate.id));
      if (!selected.length) return '';
      return `${TO_WORDING.heading}: ${selected.map((candidate) => this.getToCandidateLabel(candidate)).join('、')}\n`;
    },

    /**
     * お知らせ送信
     */
    submit(e) {
      e.preventDefault();
      if (!this.message) {
        return alert('メッセージが入力されていません。');
      }
      // To は事務局×学校共有ルームで「To を指定する」のときだけ送る（それ以外で選択値が残っていても送らない）
      const isPicking = this.isToSelectShown && this.toMode === 'pick';
      // 指定に切り替えたのに誰も選んでいなければ止める（黙って全員へ送ると「指定したつもりで全員に届いた」になる）
      if (isPicking && !this.toUserIds.length) {
        return alert(TO_WORDING.pickEmpty);
      }
      // 全員を選んでいたら「指定なし」と同じに送る（確認ダイアログに To の行を出さず、To も送らない）
      const sendsTo = isPicking && !this.isAllToCandidatesSelected({ candidates: this.toCandidates, selectedIds: this.toUserIds });
      const toUserIds = sendsTo ? [...this.toUserIds] : [];
      const toConfirmLine = sendsTo ? this.selectedToConfirmLine() : '';
      const submitConfirm = confirm(`${toConfirmLine}返信は取り消せません。よろしいですか？`);
      if (!submitConfirm) return;

      this.showLoading();

      // 必須項目
      const data = {
        group_id: this.data.id,
        messageData: {
          user_id: this.user.id,
          message: this.message,
        },
      };
      if (toUserIds.length) data.to_user_ids = toUserIds;

      this.axios({
        method: 'POST',
        url: '/v1/messageGroup/set/register',
        data,
      })
        .then(async (response) => {
          // 通知は登録した発言（message_id）の To を見て宛先を決める
          await this.operatorMessageContact(this.data.id, response.data.message_id);
          alert('メッセージを登録しました');
          // 初期化
          this.message = null;
          this.setToMode('all');
          this.hideLoading();
          this.flag.loader = true;
          this.getDetail();
        })
        .catch((error) => {
          if (error.message) console.log(error.message);
          else console.log(error);
          this.hideLoading();
          // 失敗を伝えないと、To を付けた送信が保存されなかったことに気づけない（Teacher/tabs/Messages.vue と同じ文言）
          alert('メッセージの送信に失敗しました。');
        });
    },

    /**
     * 該当者に新規メッセージの連絡
     * messageId を渡すと、サーバはその発言の To を見て「To の人＝宛先・ほかの管理者＝CC」の 1 通と
     * To の人だけへの LINE にする（To が無い発言・学校共有ルーム以外は従来どおり）。
     */
    operatorMessageContact(groupId, messageId = null) {
      // 送信する場合は送り主が事務局で、送り先が学校管理者または医師心理士の場合
      // （240205 利用者の除外を廃止）
      // if (this.user.account_type !== 31 || !([11, 12, 21, 22].includes(this.data.fromUser.account_type))) {
      //   return;
      // }
      // 240215 送信者が事務局の場合のみメッセージ連絡を発動
      if (this.user.account_type !== 31) return;

      // 対象者へ連絡
      this.axios({
        method: 'POST',
        url: '/v1/contact/regist/operatorMessage',
        data: messageId ? { group_id: groupId, message_id: messageId } : { group_id: groupId },
      })
        .then((response) => {
          if (this.helper.env.name === 'local') {
            console.log(response.data);
          }
        })
        .catch((error) => {
          if (error.message) console.log(error.message);
          else console.log(error);
        });
    },
  },
};
</script>

<style lang="scss" module>
.top {
  background-color: var(--blue-main);
  display: flex;
  justify-content: center;
  padding: 90px;
  color: #fff;
  font-weight: bold;
  font-size: 40px;
  margin: 0 calc((100vw - 100%) / (-2));
  position: relative;

  &_icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: .2;
    font-size: 130px;

    &_inner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -70%);
      font-size: 50px;
    }
  }

  &_text {
    text-align: center;
    .nowrap {
      white-space: nowrap;
    }
    @include sm-view {
      font-size: 30px;
    }
    @include xs-view {
      font-size: 20px;
    }
  }
}

.textarea {
  padding: 20px;
  background-color: var(--gray-sub);
  border: none;
  outline: none;
  width: 100%;
  border-radius: 8px;
  resize: vertical;
  min-height: 120px;
}

// To の選択欄（事務局×学校共有ルームのみ）。候補のカードは予約の「お悩みのテーマ」カードと同じ枠・選択色にそろえる
.toModes {
  display: inline-flex;
  border: 1px solid var(--orange-main);
  border-radius: 8px;
  overflow: hidden;
}
.toMode {
  padding: 6px 16px;
  border: none;
  font-size: 13px;
  color: var(--orange-main);
  background-color: #fff;
  cursor: pointer;
  & + & {
    border-left: 1px solid var(--orange-main);
  }
  &.selected {
    background-color: var(--orange-main);
    color: #fff;
    font-weight: bold;
  }
}
// 本文欄と同じ幅で 2 列（所属等がなるべく 1 行に収まる幅）。スマホは 1 人 1 行
.toCards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 10px;
  @include sm-view {
    grid-template-columns: minmax(0, 1fr);
  }
}
.toCard {
  position: relative;
  width: 100%;
  height: 100%;
  text-align: left;
  padding: 10px 36px 10px 12px;
  border: 2px solid var(--gray-main);
  border-radius: 10px;
  background-color: #fff;
  cursor: pointer;
  transition: all .2s;
  @media (hover: hover) {
    &:hover {
      border-color: var(--orange-main);
    }
  }
  &.selected {
    border-color: var(--orange-main);
    background-color: rgba(var(--orange-main-rgb), .1);
  }
}
.toCardRole {
  display: block;
  font-size: 11px;
  color: #888;
}
.toCardName {
  display: block;
  margin-top: 2px;
  font-size: 14px;
  font-weight: bold;
  // 空白の無い長い英字の氏名でも枠からはみ出さないように（和文は従来どおり 1 字単位で折り返す）
  overflow-wrap: anywhere;
}
.toCardAffiliation {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--black);
  overflow-wrap: anywhere;
}
.toCardBody {
  display: block;
}
// 氏名の横に所属等の 1 文字分（12px）空けて所属等。横に収まらないときは所属等がまとまって次の行へ回る（列の間隔は折り返した行には付かない）。
// スマホは幅が無く横に並ぶ人と並ばない人が混ざるので、これまでどおり氏名の下（顧客もスマホは今のままでよいとしている）
.toCardBodyInline {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 12px;
  @include sm-view {
    display: block;
  }
}
// 右側の丸。選ぶと塗りつぶしてチェックマークを出す
.toCardCheck {
  position: absolute;
  top: 50%;
  right: 10px;
  width: 20px;
  height: 20px;
  margin-top: -10px;
  border: 2px solid var(--gray-main);
  border-radius: 50%;
  background-color: #fff;
  .selected & {
    border-color: var(--orange-main);
    background-color: var(--orange-main);
    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 1px;
      width: 5px;
      height: 10px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}
// 発言の宛先。名前の行に「→ ○○さん（所属等）、△△さん（所属等）」と続け、自分が宛先なら氏名の代わりに「あなた」を色付きで出す
.toArrow {
  color: var(--gray-side);
  margin: 0 6px;
}
.toNames {
  font-weight: bold;
  overflow-wrap: anywhere;
}
// 宛先の所属等は投稿者の所属等（.affiliation）と同じ字にする（.toNames の太字を打ち消す）
.toAffiliation {
  font-weight: normal;
}
.toMe {
  display: inline-block;
  background-color: var(--orange-main);
  color: #fff;
  font-size: 11px;
  line-height: 1.6;
  padding: 0 7px;
  border-radius: 4px;
}
// 自分が宛先の発言は左端に同じ色の線（一覧を流し見しても拾えるように）
.question.toViewer, .answer.toViewer {
  box-shadow: inset 4px 0 0 var(--orange-main);
}

.info {
  font-size: 14px;
}
.smlabel {
  width: 150px;
}
.flex {
  display: flex;
  align-items: flex-start;

  &.center {
    align-items: center;
  }

  &.check {
    justify-content: space-between;
  }
}
.content {
  flex: 1;
}

.bold {
  font-weight: bold;
}
.label {
  @include sm-view {
    width: 180px;
  }
}
.item {
  &:not(:first-child) {
    margin-left: 10px;
  }
}
.space {
  margin-top: 8px;
  @include sm-view {
    margin-top: 0;
  }
}

.who5item {
  display: flex;
  justify-content: space-between;
  font-size: 12px;

  @include sm-view {
    flex-direction: column;
  }
}

.who5itemA {
  padding-left: var(--spacer-base);
  font-weight: bold;
  color: #444;
}

.comment {
  margin-bottom: 24px;
  &:not(:first-child) >li {
    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: -24px;
      left: 30px;
      width: 3px;
      height: 24px;
    }

    &.q {
      &::before {
        background-color: var(--blue-main);
      }
    }
    &.a {
      &::before {
        background-color: var(--orange-main);
      }
    }
  }
  .name {
    font-size: 12px;
  }
  // 無効化(flag!==1)ユーザ投稿者の氏名行をグレー化（本文は不変・#3185）
  .inactiveName {
    color: var(--gray-side);
  }
  .inactiveLabel {
    // 投稿者が現在無効化されていることを示す小ラベル（#3185）
    font-size: 11px;
    color: var(--gray-side);
    margin-left: 2px;
  }
  .affiliation {
    // 投稿者氏名の右に小フォントで F 情報を表示（UserBox.belongs 風）
    // 淡色→黒文字で視認性を上げる
    font-size: 12px;
    color: var(--black);
    @include sm-view {
      font-size: 11px;
    }
  }
  // 無効化(flag!==1)ユーザ投稿は氏名(.inactiveName)だけでなく役職/職種(F情報)もグレー化（#7）。
  // .affiliation が color:black を後勝ちで当てるため、それより後に定義して無効時のみ上書きする。
  .inactiveAffiliation {
    color: var(--gray-side);
  }
}

.question, .answer {
  // NEW バッジ(.new)の絶対配置基準（#3185）
  position: relative;
  padding: 15px;
  border-radius: 8px;
}
// 未確認メッセージの NEW バッジ（Operator/tabs/Message.vue・RiskApplicationForm.vue と同形・#3185）
.new {
  position: absolute;
  top: -3px;
  right: -8px;
  z-index: 1;
  background-color: var(--pink);
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 4px;
  @include sm-view {
    padding: 3px 7px 2px;
    font-size: 11px;
    right: -4px;
  }
}

.question {
  background-color: rgba(var(--blue-main-rgb), 0.2);
}
.answer {
  background-color: rgba(var(--orange-main-rgb), .2);
}
.message {
  margin-bottom: 24px;
}
</style>
